import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';

import { api } from '@/shared/api/http';
import type { CursorPage, UserRef } from '@/shared/api/types';
import type { NotificationItem } from '@/shared/registry/types';

/*
 * Notifications API (`/api/notifications`, docs/modules/notifications.md) and its TanStack Query hooks.
 * Query keys start with the module id: ['notifications', …].
 */

/** A notification as the API returns it (the registry's `NotificationItem` plus grouping data). */
export interface Notification extends NotificationItem {
  actor: UserRef | null;
  /** Most recent distinct actors of a grouped notification (latest first). */
  actors: UserRef[];
  /** Events merged into this notification (1 = a single event). */
  count: number;
}

export type NotificationFilter = 'all' | 'unread';

export interface NotificationListParams {
  filter: NotificationFilter;
  module: string | null;
}

export interface NotificationModule {
  module: string;
  total: number;
  unread: number;
}

export interface BulkResult {
  affected: number;
  unreadCount: number;
}

export type EmailFrequency = 'instant' | 'hourly' | 'daily' | 'off';

/** The signed-in user's own latest notification of a type, shown as an example of it (never another user's). */
export interface NotificationTypeExample {
  params: Record<string, string>;
  fallbackText: string;
  occurredAt: string;
}

export interface NotificationTypeSetting {
  type: string;
  module: string;
  inApp: boolean;
  email: boolean;
  /** Web push to the user's devices (docs/modules/notifications.md "Web push"). */
  push: boolean;
  isCustomized: boolean;
  /** Null when the user has no notification of this type. */
  example: NotificationTypeExample | null;
}

export interface NotificationSettings {
  emailFrequency: EmailFrequency;
  email: string | null;
  emailByDefault: boolean;
  types: NotificationTypeSetting[];
}

export interface UpdateNotificationSettings {
  emailFrequency?: EmailFrequency;
  /** `push` may be left out: the stored choice stays. */
  types?: { type: string; inApp: boolean; email: boolean; push?: boolean }[];
}

/** Realtime: a notification arrived (or a grouped one got another event). */
export const NOTIFICATION_CREATED = 'notifications.notification-created';
/** Realtime: read state or the set of notifications changed elsewhere (another tab or device). */
export const INBOX_CHANGED = 'notifications.inbox-changed';

export interface NotificationCreatedPayload {
  /** Null for very large audiences: refetch instead. */
  notification: Notification | null;
  unreadCount: number | null;
  grouped: boolean;
}

export interface InboxChangedPayload {
  unreadCount: number;
}

export const PAGE_SIZE = 20;

export const notificationKeys = {
  all: ['notifications'] as const,
  lists: ['notifications', 'list'] as const,
  list: (params: NotificationListParams) => ['notifications', 'list', params] as const,
  unreadCount: ['notifications', 'unread-count'] as const,
  modules: ['notifications', 'modules'] as const,
  settings: ['notifications', 'settings'] as const,
};

export const fetchNotifications = (
  params: NotificationListParams,
  cursor: string | null,
  signal?: AbortSignal,
) =>
  api.get<CursorPage<Notification>>('/notifications', {
    query: {
      filter: params.filter,
      module: params.module,
      cursor,
      limit: PAGE_SIZE,
    },
    signal,
  });

export function useNotifications(params: NotificationListParams, options: { enabled?: boolean } = {}) {
  return useInfiniteQuery({
    queryKey: notificationKeys.list(params),
    queryFn: ({ pageParam, signal }) => fetchNotifications(params, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: options.enabled ?? true,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount,
    queryFn: ({ signal }) => api.get<{ count: number }>('/notifications/unread-count', { signal }),
    select: (data) => data.count,
    staleTime: 60_000,
  });
}

export function useNotificationModules() {
  return useQuery({
    queryKey: notificationKeys.modules,
    queryFn: ({ signal }) => api.get<NotificationModule[]>('/notifications/modules', { signal }),
  });
}

/* ---------- cache helpers (optimistic updates and realtime) ---------- */

type ListData = InfiniteData<CursorPage<Notification>, string | null>;

function listParams(key: readonly unknown[]): NotificationListParams | null {
  const params = key[2];
  return params && typeof params === 'object' ? (params as NotificationListParams) : null;
}

/** Applies `update` to every item of every cached list (`null` removes the item). */
export function updateCachedNotifications(
  client: QueryClient,
  update: (item: Notification, params: NotificationListParams) => Notification | null,
): void {
  for (const [key, data] of client.getQueriesData<ListData>({ queryKey: notificationKeys.lists })) {
    const params = listParams(key);
    if (!data || !params) continue;
    client.setQueryData<ListData>(key, {
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        items: page.items.flatMap((item) => {
          const next = update(item, params);
          return next ? [next] : [];
        }),
      })),
    });
  }
}

/** Puts a new or regrouped notification on top of every matching cached list. */
export function prependCachedNotification(client: QueryClient, notification: Notification): void {
  for (const [key, data] of client.getQueriesData<ListData>({ queryKey: notificationKeys.lists })) {
    const params = listParams(key);
    if (!data || !params) continue;
    const matches =
      (params.module === null || params.module === notification.module) &&
      (params.filter === 'all' || !notification.isRead);
    const pages = data.pages.map((page, index) => {
      const items = page.items.filter((item) => item.id !== notification.id);
      return { ...page, items: index === 0 && matches ? [notification, ...items] : items };
    });
    client.setQueryData<ListData>(key, { ...data, pages });
  }
}

export function setUnreadCount(client: QueryClient, count: number): void {
  client.setQueryData(notificationKeys.unreadCount, { count: Math.max(0, count) });
}

function adjustUnreadCount(client: QueryClient, delta: number): void {
  const current = client.getQueryData<{ count: number }>(notificationKeys.unreadCount);
  if (current) setUnreadCount(client, current.count + delta);
}

function findCached(client: QueryClient, id: string): Notification | undefined {
  for (const [, data] of client.getQueriesData<ListData>({ queryKey: notificationKeys.lists })) {
    const found = data?.pages.flatMap((p) => p.items).find((item) => item.id === id);
    if (found) return found;
  }
  return undefined;
}

interface Snapshot {
  lists: [readonly unknown[], ListData | undefined][];
  count: { count: number } | undefined;
}

async function snapshot(client: QueryClient): Promise<Snapshot> {
  await client.cancelQueries({ queryKey: notificationKeys.all });
  return {
    lists: client.getQueriesData<ListData>({ queryKey: notificationKeys.lists }),
    count: client.getQueryData<{ count: number }>(notificationKeys.unreadCount),
  };
}

function restore(client: QueryClient, saved: Snapshot | undefined): void {
  if (!saved) return;
  for (const [key, data] of saved.lists) client.setQueryData(key, data);
  if (saved.count) client.setQueryData(notificationKeys.unreadCount, saved.count);
}

function settle(client: QueryClient): void {
  void client.invalidateQueries({ queryKey: notificationKeys.unreadCount });
  void client.invalidateQueries({ queryKey: notificationKeys.modules });
}

/* ---------- mutations (optimistic) ---------- */

export function useMarkRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, read }: { id: string; read: boolean }) =>
      api.post(`/notifications/${encodeURIComponent(id)}/${read ? 'read' : 'unread'}`),
    onMutate: async ({ id, read }) => {
      const saved = await snapshot(client);
      const before = findCached(client, id);
      updateCachedNotifications(client, (item, params) =>
        item.id !== id
          ? item
          : read && params.filter === 'unread'
            ? { ...item, isRead: true }
            : { ...item, isRead: read },
      );
      if (before && before.isRead !== read) adjustUnreadCount(client, read ? -1 : 1);
      return saved;
    },
    onError: (_error, _variables, saved) => restore(client, saved),
    onSettled: () => settle(client),
  });
}

export function useDeleteNotification() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/notifications/${encodeURIComponent(id)}`),
    onMutate: async (id) => {
      const saved = await snapshot(client);
      const before = findCached(client, id);
      updateCachedNotifications(client, (item) => (item.id === id ? null : item));
      if (before && !before.isRead) adjustUnreadCount(client, -1);
      return saved;
    },
    onError: (_error, _id, saved) => restore(client, saved),
    onSettled: () => settle(client),
  });
}

export function useMarkAllRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (module: string | null) => api.post<BulkResult>('/notifications/read-all', { module }),
    onMutate: async (module) => {
      const saved = await snapshot(client);
      updateCachedNotifications(client, (item) =>
        module === null || item.module === module ? { ...item, isRead: true } : item,
      );
      if (module === null) setUnreadCount(client, 0);
      return saved;
    },
    onSuccess: (result) => setUnreadCount(client, result.unreadCount),
    onError: (_error, _module, saved) => restore(client, saved),
    onSettled: () => {
      settle(client);
      void client.invalidateQueries({ queryKey: notificationKeys.lists });
    },
  });
}

export function useDeleteRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (module: string | null) =>
      api.delete<BulkResult>('/notifications/read', { query: { module } }),
    onMutate: async (module) => {
      const saved = await snapshot(client);
      updateCachedNotifications(client, (item) =>
        item.isRead && (module === null || item.module === module) ? null : item,
      );
      return saved;
    },
    onError: (_error, _module, saved) => restore(client, saved),
    onSettled: () => {
      settle(client);
      void client.invalidateQueries({ queryKey: notificationKeys.lists });
    },
  });
}

/* ---------- settings ---------- */

export function useNotificationSettings() {
  return useQuery({
    queryKey: notificationKeys.settings,
    queryFn: ({ signal }) => api.get<NotificationSettings>('/notifications/settings', { signal }),
  });
}

export function useUpdateNotificationSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (request: UpdateNotificationSettings) =>
      api.put<NotificationSettings>('/notifications/settings', request),
    onMutate: async (request) => {
      await client.cancelQueries({ queryKey: notificationKeys.settings });
      const saved = client.getQueryData<NotificationSettings>(notificationKeys.settings);
      if (saved) {
        const changes = new Map((request.types ?? []).map((t) => [t.type, t]));
        client.setQueryData<NotificationSettings>(notificationKeys.settings, {
          ...saved,
          emailFrequency: request.emailFrequency ?? saved.emailFrequency,
          types: saved.types.map((t) => {
            const change = changes.get(t.type);
            return change
              ? { ...t, inApp: change.inApp, email: change.email, push: change.push ?? t.push, isCustomized: true }
              : t;
          }),
        });
      }
      return saved;
    },
    onSuccess: (settings) => client.setQueryData(notificationKeys.settings, settings),
    onError: (_error, _request, saved) => {
      if (saved) client.setQueryData(notificationKeys.settings, saved);
    },
  });
}

export function useResetNotificationSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<NotificationSettings>('/notifications/settings/reset'),
    onSuccess: (settings) => client.setQueryData(notificationKeys.settings, settings),
  });
}

export function useSendTestNotification() {
  return useMutation({ mutationFn: () => api.post('/notifications/test') });
}
